import { useFormContext } from 'react-hook-form';

import { DetailFormDatePicker, DetailFormDecimal, DetailFormTextArea, DetailValue } from 'components/DetailsView';
import { SectionContainer } from 'components/DetailsView/Section';

import {
    computeHourDeliveryFromDate,
    dateFromHourString,
    formatDateAsAnniversary,
    dateFromString,
    getExtendedFormattedDatetime,
} from 'helpers/dateTimes';
import { formatEuroAmount } from 'helpers/price';

import { SupplyOrderState } from 'data/__generated__';

import { SupplyOrderDetails, SupplyOrderFormValues } from '../supplyOrderFormValues';

/** Réception et facturation : ce qu'on constate à l'arrivée, et ce qu'Odoo nous renvoie de la facture. */
export const ReceiptSection = ({ supplyOrder }: { supplyOrder: SupplyOrderDetails }) => {
    const { control, register } = useFormContext<SupplyOrderFormValues>();
    const isInRack = supplyOrder.state === SupplyOrderState.InRack;

    return (
        <SectionContainer title="Réception et facturation">
            <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-x-8 gap-y-4">
                <DetailFormDatePicker
                    label="Livré le"
                    placeholder="Date de livraison"
                    name="dateDelivery"
                    defaultValue={supplyOrder.dateDelivery}
                    control={control}
                    transformValueToDate={(value) => (value ? dateFromString(value) : null)}
                    transformDateToValue={(date) => (date ? formatDateAsAnniversary({ dateTime: date }) : null)}
                    isClearable={!isInRack}
                    isDisabled={isInRack}
                />
                <DetailFormDatePicker
                    label="Heure de livraison"
                    placeholder="Heure de livraison"
                    name="hourDelivery"
                    defaultValue={supplyOrder.hourDelivery}
                    control={control}
                    transformValueToDate={(value) => (value ? dateFromHourString(value) : null)}
                    transformDateToValue={(date) => (date ? computeHourDeliveryFromDate(date) : null)}
                    dateFormat="HH:mm"
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={5}
                    isClearable={!isInRack}
                    isDisabled={isInRack}
                />
                {/* Décimales libres : une température se relève au dixième, une facture se recopie au
                    centime — et parfois au millième sur les prix unitaires. */}
                <DetailFormDecimal label="Température à la réception" name="temperature" control={control} unit="°C" />

                <DetailFormDecimal label="Prix HT facture" name="priceHTInvoice" control={control} unit="€" />
                <DetailFormDecimal label="Prix TTC facture" name="priceTTCInvoice" control={control} unit="€" />

                {/* Remontés par le cron de collecte des factures fournisseur Odoo : en lecture seule. */}
                <DetailValue label="Numéro de facture" value={supplyOrder.invoiceNumber ?? 'Pas encore facturé'} />
                <DetailValue
                    label="Date de facturation"
                    value={
                        supplyOrder.dateBilling
                            ? getExtendedFormattedDatetime(supplyOrder.dateBilling)
                            : 'Pas encore facturé'
                    }
                />
                <DetailValue label="Total HT commandé" value={formatEuroAmount(supplyOrder.priceHT)} />

                <DetailFormTextArea
                    label="Commentaires de réception"
                    defaultValue={supplyOrder.description ?? ''}
                    {...register('description')}
                />
            </div>
        </SectionContainer>
    );
};
